import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { ApiError } from '../api/client';
import { useMe } from '../auth/context';
import { Card } from '../components/Card';
import { ErrorMessage } from '../components/ErrorMessage';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { addressLine, appointmentStatusLabels, fmtDateTime, fmtTime, fromLocal, outcomeLabels, toLocalParts } from '../ops/format';
import { ContractConfiguratorModal } from '../documents/ContractConfiguratorModal';
import { DocumentsArea } from '../documents/DocumentsArea';
import { ConfirmationCard } from '../ops/ConfirmationCard';
import { ConversationInsights } from '../ops/ConversationInsights';
import { AppointmentHistoryPanel, HandbackBanner, HandbackForm, ReassignPanel } from '../ops/AppointmentHandback';
import { AppointmentOpenItems } from '../ops/OpenItems';
import { TicketPanel } from '../ops/TicketPanel';
import { useFieldOnly, useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { isOffice } from '../pricing/format';
import { briefRows } from '../ops/briefRows';
import { cleanAiText, objectFacts } from '../ops/aiText';
import { PhoneConfirmationCard } from '../ops/PhoneConfirmationCard';
import { LeadContactEditById } from './leads/LeadContactEditById';
import { CancelNextChoice } from '../ops/CancelNextChoice';
import { type CancelNextValue, cancelNextComplete } from '../ops/cancelNext';
import { LeadNotes } from './leads/LeadNotes';
import { ReportCard } from '../ops/report/ReportCard';
import { ReportWizard } from '../ops/report/ReportWizard';
import { MandateSection } from '../pricing/MandateSection';

// AppointmentDetailPage: alles, was der Außendienst vor der Tür braucht – Kurzfassung zuerst. Makler ändern Termine
// nicht selbst: Können sie nicht, geben sie den Termin an den Support zurück; Verschieben, Makler wechseln und Absagen
// sind Aufgaben der Terminplanung (Support/Büro).
export function AppointmentDetailPage() {
  const { appointmentId = '' } = useParams();
  const { project, can } = useProject();
  const me = useMe();
  const fieldOnly = useFieldOnly();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const key = projectKeys.appointment(project.id, appointmentId);
  const detail = useQuery({ queryKey: key, queryFn: ({ signal }) => papi.appointment(appointmentId, signal) });
  // Objektangaben für „Auf einen Blick“ (Hauptobjekt des Leads).
  const objectsLeadId = detail.data && detail.data.purpose !== 'VIEWING' ? (detail.data.lead_id ?? '') : '';
  const objects = useQuery({
    queryKey: projectKeys.leadObjects(project.id, objectsLeadId),
    queryFn: ({ signal }) => papi.leadObjects(objectsLeadId, signal),
    enabled: !!objectsLeadId && can('lead.read'),
  });
  const [panel, setPanel] = useState<'' | 'reschedule' | 'cancel' | 'inquiry' | 'handback' | 'reassign' | 'configurator'>('');
  const [text, setText] = useState('');
  const [when, setWhen] = useState({ date: '', time: '' });
  const [allowConflicts, setAllowConflicts] = useState(false);
  const [cancelNext, setCancelNext] = useState<{ next?: CancelNextValue; later_date?: string }>({});
  const [now] = useState(() => new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  // Fragenkatalog nach dem Termin (B-29 Phase 2): aus der Erinnerung („?nachbereiten=1“) öffnet er sich von selbst.
  const [reporting, setReporting] = useState(() => searchParams.get('nachbereiten') === '1');
  // Terminbericht (für den zuständigen Makler und die Terminplanung): nach dem Abschicken als Karte mit KI-Auswertung.
  const d = detail.data;
  const reportVisible = !!d && d.purpose !== 'VIEWING' && !!d.lead_id && new Date(d.start_at) <= now && can('appointment.edit') &&
    (d.assigned_field_sales_user_id === me.user.id || can('appointment.reschedule'));
  const reportQ = useQuery({
    queryKey: projectKeys.appointmentReport(project.id, appointmentId),
    queryFn: ({ signal }) => papi.appointmentReport(appointmentId, signal),
    enabled: reportVisible,
    retry: false,
  });
  const submitted = reportQ.data?.status === 'SUBMITTED';
  const done = () => {
    setPanel('');
    setText('');
    void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
  };
  const dossier = useMutation({ mutationFn: () => papi.sendAppointmentDossier(appointmentId) });
  const cancel = useMutation({ mutationFn: () => papi.cancelAppointment(appointmentId, text, cancelNext), onSuccess: done });
  const inquiry = useMutation({ mutationFn: () => papi.appointmentInquiry(appointmentId, text), onSuccess: done });
  const reschedule = useMutation({
    mutationFn: () => papi.rescheduleAppointment(appointmentId, { start_at: fromLocal(when.date, when.time), reason: text, allow_conflicts: allowConflicts || undefined }),
    onSuccess: done,
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'appointment_conflict') setAllowConflicts(true);
    },
  });

  if (detail.isPending) return <PageLoader label="Termin wird geladen …" />;
  if (detail.isError) return <ErrorMessage error={detail.error} onRetry={() => void detail.refetch()} />;
  const a = detail.data;
  const brief = a.brief?.content;
  const active = ['PLANNED', 'NEEDS_CONFIRMATION', 'CONFIRMED', 'RESCHEDULED'].includes(a.status);
  const lead = a.lead;
  // Wer den Termin vereinbart hat (für die Notiz oben) – der Anruf mit dem Ergebnis „Termin vereinbart“.
  const booker = a.recent_calls.find((c) => c.outcome === 'APPOINTMENT')?.user_name ?? '';
  const facts = objectFacts((objects.data?.items ?? []).find((o) => o.is_main));
  // Besichtigung: Ansprechpartner ist der Interessent, der Lead ist das Objekt (Eigentümer).
  const viewing = a.purpose === 'VIEWING';
  const mutationError = cancel.error ?? inquiry.error ?? reschedule.error ?? dossier.error;
  const mine = a.assigned_field_sales_user_id === me.user.id;
  // Knöpfe nach Zeitpunkt (Dominik 12.10.2026): vor dem Termin Rückgabe bzw. Planung, ab Terminbeginn das Ergebnis.
  const started = new Date(a.start_at) <= now;
  // Nachbereiten darf der zuständige Makler oder die Terminplanung – bei Eigentümerterminen mit Lead, ab Terminbeginn.
  const canReport =
    !submitted && started && !viewing && !!a.lead_id && a.status !== 'CANCELED' && can('appointment.edit') && (mine || can('appointment.reschedule'));
  const closeReport = () => {
    setReporting(false);
    if (searchParams.has('nachbereiten')) {
      searchParams.delete('nachbereiten');
      setSearchParams(searchParams, { replace: true });
    }
  };
  const canPlan = can('appointment.reschedule');
  // Terminmappe (PDF per E-Mail an den zuständigen Makler): geht bei jedem neuen Termin automatisch raus, hier erneut.
  const canDossier = active && !viewing && !!a.assigned_field_sales_user_id && (mine || canPlan);
  const openReschedule = () => {
    setPanel('reschedule');
    setWhen(toLocalParts(a.handback?.proposed_start_at ?? a.start_at));
  };
  const docsFirst = fieldOnly && started && !!a.lead_id && can('document.read');
  const prep = brief ? briefRows(brief, ['Gespräch', 'Letzter Kontakt', 'Ziel']) : [];
  const contacts =
    a.recent_calls.length === 0 ? (
      <p className="muted">Keine Anrufe.</p>
    ) : (
      <ul className="plain-list call-history">
        {a.recent_calls.map((c) => (
          <li key={c.id}>
            <strong>{fmtDateTime(c.created_at)}</strong> {c.outcome ? outcomeLabels[c.outcome as keyof typeof outcomeLabels] : c.status}
            {c.user_name && <span className="muted"> · {c.user_name}</span>}
          </li>
        ))}
      </ul>
    );

  return (
    <div className="page appointment-detail">
      <header className="page-header">
        <div>
          <p className="eyebrow">
            {a.type_name ?? 'Termin'} · {appointmentStatusLabels[a.status]}
          </p>
          <h1>{viewing ? a.title : (a.lead_name ?? a.title)}</h1>
          <p className="muted">
            {fmtDateTime(a.start_at)} – {fmtTime(a.end_at)}
            {a.field_sales_name && ` · ${a.field_sales_name}`}
          </p>
          {a.goal_level === 'PARTIAL' && (
            <p className="goal-hint">
              <span className="badge badge-warn">80 %</span> {a.type_name ?? 'Telefontermin'} – für diesen Makler nur die zweitbeste Stufe, Ziel ist der
              Termin vor Ort.
            </p>
          )}
          {a.external_source === 'BOOKINGS' && (
            <p className="muted">Gebucht über Microsoft Bookings – Kundenbenachrichtigung und Kalendereintrag des Mitarbeiters führt Bookings.</p>
          )}
        </div>
        {canDossier && (
          <div className="form-actions">
            {dossier.isSuccess ? (
              <p className="muted" role="status">
                Terminmappe wird an {dossier.data.recipient || 'den Makler'} geschickt – die E-Mail kommt in wenigen Sekunden.
              </p>
            ) : (
              <button type="button" className="btn" disabled={dossier.isPending} onClick={() => dossier.mutate()}>
                <Icon name="mail" /> {mine ? 'Terminmappe an mich senden' : 'Terminmappe an den Makler senden'}
              </button>
            )}
          </div>
        )}
      </header>
      <AppointmentOpenItems appointmentId={a.id} />
      {!(fieldOnly && started) && <PhoneConfirmationCard appointment={a} />}
      {!viewing && a.status !== 'CANCELED' && !!a.assigned_field_sales_user_id && started && (mine || isOffice(can)) && <MandateSection appointmentId={a.id} />}
      {(brief || a.notes || facts.object) && (
        <section className="card brief-short" aria-label="Auf einen Blick">
          <p className="eyebrow">Auf einen Blick</p>
          {brief && <p className="brief-text">{cleanAiText(brief.short)}</p>}
          {(facts.object || facts.price || brief?.desired_outcome) && (
            <dl className="facts">
              {facts.object && (
                <>
                  <dt>Objekt</dt>
                  <dd>{facts.object}</dd>
                </>
              )}
              {facts.price && (
                <>
                  <dt>Preisvorstellung</dt>
                  <dd>{facts.price}</dd>
                </>
              )}
              {brief?.desired_outcome && (
                <>
                  <dt>Ziel des Termins</dt>
                  <dd>{cleanAiText(brief.desired_outcome)}</dd>
                </>
              )}
            </dl>
          )}
          {a.notes && (
            <div className="booking-note">
              <p className="eyebrow">Notiz {booker ? `von ${booker} ` : ''}bei der Terminvereinbarung</p>
              <p>{a.notes}</p>
            </div>
          )}
          {a.brief?.generated_by === 'AI' && (
            <small className="muted">Kurzfassung aus der KI-Auswertung des Telefonats – Angaben des Eigentümers bitte im Termin bestätigen.</small>
          )}
        </section>
      )}
      <div className="detail-grid">
        <section className="card">
          <h2>Kontakt & Ort</h2>
          <dl className="facts">
            <dt>{viewing ? 'Interessent' : 'Ansprechpartner'}</dt>
            <dd>
              {a.contact_name ?? '–'}
              {brief?.role && <span className="muted"> · {brief.role}</span>}
            </dd>
            <dt>Telefon</dt>
            <dd>{a.contact_phone ? <a href={`tel:${a.contact_phone}`}>{a.contact_phone}</a> : '–'}</dd>
            {viewing && (
              <>
                <dt>Objekt</dt>
                <dd>
                  {a.object_number && `${a.object_number} · `}
                  {a.lead_id ? <Link to={`/p/${project.key}/leads/${a.lead_id}`}>{a.lead_name ?? 'Eigentümer'}</Link> : (a.lead_name ?? '–')}
                </dd>
              </>
            )}
            {!viewing && lead?.email && (
              <>
                <dt>E-Mail</dt>
                <dd>
                  <a href={`mailto:${lead.email}`}>{lead.email}</a>
                </dd>
              </>
            )}
            <dt>Adresse</dt>
            <dd>
              {addressLine(a)}
              {a.maps_url && (
                <>
                  {' '}
                  <a className="btn btn-small btn-navigate" href={a.maps_url} target="_blank" rel="noreferrer">
                    <Icon name="navigate" size={16} /> Navigation starten
                  </a>
                </>
              )}
            </dd>
            {!fieldOnly && (
              <>
                <dt>Landkreis / Gebiet</dt>
                <dd>{[a.county_name, a.area_name].filter(Boolean).join(' · ') || '–'}</dd>
              </>
            )}
          </dl>
          {a.address_differs_from_lead && <p className="alert alert-warn">Die Terminadresse weicht von der Lead-Adresse ab.</p>}
          {a.geo_status !== 'VALID' && a.geo_status !== 'NOT_REQUIRED' && <p className="alert alert-warn">Adresse konnte nicht bestätigt werden.</p>}
          {!viewing && a.lead_id && <LeadContactEditById leadId={a.lead_id} />}
        </section>
        {prep.length > 0 && (
          <section className="card">
            <h2>Terminvorbereitung</h2>
            <dl className="facts prose">
              {prep.map(([label, text]) => (
                <Fragment key={label}>
                  <dt>{label}</dt>
                  <dd>{text}</dd>
                </Fragment>
              ))}
            </dl>
          </section>
        )}
        {!fieldOnly && <Card title="Bisherige Kontakte">{contacts}</Card>}
      </div>
      {submitted && reportQ.data && <ReportCard report={reportQ.data} />}
      {docsFirst && a.lead_id && <DocumentsArea leadId={a.lead_id} appointmentId={a.id} />}
      {/* Erlebnisse aus dem Termin festhalten – vor, während und nach dem Termin, auch wenn er schon abgeschlossen ist. */}
      {!viewing && a.lead_id && (
        <section className="card">
          <LeadNotes
            leadId={a.lead_id}
            notes={lead?.notes}
            appointmentId={a.id}
            placeholder="z. B. wie der Termin lief, was vereinbart wurde, wie wir verblieben sind"
            hint="Erlebnisse aus dem Termin hier festhalten – auch nachträglich. Support und Terminierer sehen sie am Lead und in der Historie."
          />
        </section>
      )}
      {a.conflicts.length > 0 && (
        <div className="alert alert-warn">
          <strong>Hinweise zur Planung</strong>
          <ul className="plain-list">
            {a.conflicts.map((c) => (
              <li key={`${c.type}-${c.start_at}`}>{c.message}</li>
            ))}
          </ul>
        </div>
      )}
      {active && a.handback && (
        <HandbackBanner
          appointment={a}
          handback={a.handback}
          isRequester={a.handback.requested_by_user_id === me.user.id}
          onReschedule={openReschedule}
          onReassign={() => setPanel('reassign')}
          onCancel={() => setPanel('cancel')}
        />
      )}
      {active && (
        <div className="form-actions sticky-actions">
          {canReport && (
            <button type="button" className="btn btn-primary" onClick={() => setReporting(true)}>
              Termin nachbereiten
            </button>
          )}
          {can('appointment.handback') && mine && !a.handback && !started && (
            <button type="button" className="btn" onClick={() => setPanel('handback')}>
              Kann ich nicht wahrnehmen
            </button>
          )}
          {canPlan && !a.handback && (
            <>
              <button type="button" className="btn" onClick={openReschedule}>
                Verschieben
              </button>
              <button type="button" className="btn" onClick={() => setPanel('reassign')}>
                Makler wechseln
              </button>
            </>
          )}
          {can('appointment.cancel') && !a.handback && (
            <button type="button" className="btn btn-danger" onClick={() => setPanel('cancel')}>
              Absagen
            </button>
          )}
          {!viewing && a.lead_id && can('document.edit') && (
            <button type="button" className="btn btn-primary" onClick={() => setPanel('configurator')}>
              Auftrags-Konfigurator (Slider)
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={() => setPanel('inquiry')}>
            Rückfrage an Support
          </button>
          {can('lead.read') && (
            <Link className="btn btn-ghost" to={`/p/${project.key}/leads/${a.lead_id}`}>
              Lead öffnen
            </Link>
          )}
        </div>
      )}
      {!active && (canReport || (can('lead.read') && a.lead_id)) && (
        <div className="form-actions">
          {canReport && (
            <button type="button" className="btn btn-primary" onClick={() => setReporting(true)}>
              Termin nachbereiten
            </button>
          )}
          {!viewing && a.lead_id && can('document.edit') && (
            <button type="button" className="btn" onClick={() => setPanel('configurator')}>
              Auftrags-Konfigurator (Slider)
            </button>
          )}
          {can('lead.read') && a.lead_id && (
            <Link className="btn btn-ghost" to={`/p/${project.key}/leads/${a.lead_id}`}>
              Lead öffnen
            </Link>
          )}
        </div>
      )}
      {panel === 'configurator' && a.lead_id && (
        <ContractConfiguratorModal leadId={a.lead_id} appointmentId={a.id} onClose={() => setPanel('')} />
      )}
      {reporting && canReport && (
        <ReportWizard appointmentId={a.id} title={`${a.lead_name ?? a.title}, ${fmtDateTime(a.start_at)}`} goal={a.appointment_goal} onClose={closeReport} />
      )}
      {panel === 'handback' && <HandbackForm appointment={a} onDone={done} onCancel={() => setPanel('')} />}
      {panel === 'reassign' && <ReassignPanel appointment={a} onDone={done} onCancel={() => setPanel('')} />}
      {panel && panel !== 'handback' && panel !== 'reassign' && (
        <form
          className="card inline-panel"
          onSubmit={(e) => {
            e.preventDefault();
            if (panel === 'cancel') cancel.mutate();
            else if (panel === 'inquiry') inquiry.mutate();
            else reschedule.mutate();
          }}
        >
          {panel === 'reschedule' && (
            <div className="inline-form">
              <label className="field narrow">
                <span>Neues Datum</span>
                <input type="date" value={when.date} onChange={(e) => setWhen({ ...when, date: e.target.value })} required />
              </label>
              <label className="field narrow">
                <span>Uhrzeit</span>
                <input type="time" value={when.time} step={900} onChange={(e) => setWhen({ ...when, time: e.target.value })} required />
              </label>
            </div>
          )}
          <label className="field wide">
            <span>{panel === 'cancel' ? 'Grund der Absage (Pflicht)' : panel === 'inquiry' ? 'Ihre Rückfrage' : 'Grund (optional)'}</span>
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} required={panel !== 'reschedule'} />
          </label>
          {panel === 'cancel' && !viewing && a.lead_id && (
            <CancelNextChoice value={cancelNext.next} laterDate={cancelNext.later_date} onChange={(next, later) => setCancelNext({ next, later_date: later })} />
          )}
          {mutationError && <MutationError error={mutationError} />}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                cancel.isPending ||
                inquiry.isPending ||
                reschedule.isPending ||
                (panel === 'cancel' && !viewing && !!a.lead_id && !cancelNextComplete(cancelNext.next, cancelNext.later_date))
              }
            >
              {panel === 'cancel' ? 'Termin absagen' : panel === 'inquiry' ? 'Rückfrage senden' : allowConflicts ? 'Trotz Konflikt verschieben' : 'Verschieben'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setPanel('')}>
              Abbrechen
            </button>
          </div>
        </form>
      )}
      {inquiry.isSuccess && (
        <p className="alert alert-success">
          Rückfrage {inquiry.data.reference} wurde an den Support gesendet. Antworten erscheinen hier und als Benachrichtigung.
        </p>
      )}
      {!viewing && a.lead_id && <ConversationInsights leadId={a.lead_id} collapsed />}
      {a.lead_id && a.external_source !== 'BOOKINGS' && <ConfirmationCard appointmentId={a.id} viewing={viewing} compact={fieldOnly} />}
      {a.lead_id && can('document.read') && !docsFirst && <DocumentsArea leadId={a.lead_id} appointmentId={a.id} collapsed={fieldOnly || undefined} />}
      <div id="rueckfragen">
        <TicketPanel appointmentId={a.id} leadId={a.lead_id ?? undefined} title="Rückfragen zum Termin" hideWhenEmpty={fieldOnly} />
      </div>
      {fieldOnly && (
        <Card title="Bisherige Kontakte" collapsible>
          {contacts}
        </Card>
      )}
      <AppointmentHistoryPanel appointmentId={a.id} collapsed={fieldOnly} />
    </div>
  );
}
